import { usePageActions, PageContext } from "../../PageContent";
import { BlockButton, Breakpoints, ConfirmationModal, Icon, IconAndText, IconDefinitions, ODataGrid, ResponsiveGridColumn, SpinnerDefault, TitlelessCard } from "../../../components";
import { useCallback, useRef, useState } from "react";
import { ODataResponse, ModuleModel } from "../../../data/entities";
import { useOutletContext } from "react-router-dom";
import { AddModuleModal, EditModuleModal } from "./components";
import { useSharedState } from "../../../app/sharedProperty";
import { showErrorMessage, showSuccessMessage } from "../../../app/tools";
import { MenuItem, MenuSelectEvent } from "@progress/kendo-react-layout";
import { ModuleService } from "../../../services/ModuleService";
import { GridCellProps } from "@progress/kendo-react-grid";

export function SettingsModulesCard() {

  const context: PageContext = useOutletContext();
  context.setTitle("Settings: Modules");
  
  const gridData = useSharedState<Array<ModuleModel>>([]);
  const gridRef = useRef<ODataGrid>(null);
  const showAddState = useSharedState(false);
  const showEditState = useSharedState(false);
  const showDeleteState = useSharedState(false);
  const editItemState = useSharedState<ModuleModel>({} as ModuleModel)
  const deleteItemState = useSharedState<ModuleModel>({} as ModuleModel)
  const [showSpinner, setShowSpinner] = useState(false);

  const moduleService = new ModuleService();

  usePageActions(
    <BlockButton onClick={() => { showAddState.setter(true); }}>
      <IconAndText iconName={IconDefinitions.add} text="Add New Module" />
    </BlockButton>
  );

  const getModules = (oDataQuery: string) =>
    moduleService.getModulesAsync(oDataQuery);

  const onRowMenuSelect = (e: MenuSelectEvent, dataItem: any) => {
    if (dataItem.isDeleted) return;
    switch (e.item.data.action){
      case "edit":
        showEdit(dataItem);
        break;
      case "delete":
        showDelete(dataItem);
    }
  }

  const showEdit = (dataItem: ModuleModel) => {
    editItemState.setter(dataItem);
    showEditState.setter(true);
  }

  const showDelete = (dataItem: ModuleModel) => {
    deleteItemState.setter(dataItem);
    showDeleteState.setter(true);
  }

  const addModule = async (module: ModuleModel) => {
    try {
      showAddState.setter(false);
      setShowSpinner(true);
      await moduleService.addModuleAsync(module);
      gridRef.current?.refreshData();
      showSuccessMessage("Module added successfully.");
    } catch (err) {
      throw new Error(`Unable to add new module, ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const editModule = async (module: ModuleModel) => {
    setShowSpinner(true);
    try{
      showEditState.setter(false);
      await moduleService.editModuleAsync(module);
      gridRef.current?.refreshData();
      showSuccessMessage("Module updated successfully.");
    } catch (err) {
      throw new Error(`Unable to edit module: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const deleteModule = async (module: ModuleModel) => {
    setShowSpinner(true);
    try {
      const response = await moduleService.deleteModuleByIdAsync(module.id);
      gridRef.current?.refreshData();
      if (response) {
        showSuccessMessage("Module deleted successfully.");
      } else {
        showErrorMessage("Unable to delete module.");
      }
    } catch(err) {
      throw new Error(`Unable to delete module: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  return (
    <>
      <SpinnerDefault show={showSpinner} fullscreen={true} />
      <TitlelessCard bodyClassName="p-0">
        <ODataGrid
          ref={gridRef}
          fullHeight={true}
          getData={getModules}
          dataState={gridData}
          onRowMenuSelect={onRowMenuSelect}
          onRowClick={(e) => { showEdit(e.dataItem); }}
          sort={[{ field: "name", dir: "desc"}]}
          scrollable="scrollable"
          pageSize={100}
        >
          <ResponsiveGridColumn
            title="Name"
            field="name"
            sortable={true}
          />
          <ResponsiveGridColumn
            title="Code"
            field="code"
            sortable={true}
          />
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={commandsCell}
            width={50}
            breakpoint={Breakpoints.sm}
          />
          <MenuItem
            text="Edit"
            data={{ action: "edit" }}
            icon="hyperlink-open"
          />
          <MenuItem
            text="Delete"
            data={{ action: "delete" }}
            icon={IconDefinitions.delete}
          />
        </ODataGrid>
      </TitlelessCard>
      <AddModuleModal
        showState={showAddState}
        onSubmit={addModule}
      />
      <EditModuleModal
        showState={showEditState}
        dataItemState={editItemState}
        onSubmit={editModule}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete Module ${deleteItemState.value?.name}`}
        message={`You are about to delete this module, it cannot be undone. Are you sure you want to delete module ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteModule(deleteItemState.value)}
      />
    </>
  );
}

export default SettingsModulesCard;

